import { useState, useRef, useEffect } from "react";
import singletonRouter from "next/router";
import { getDelocalizedPath, useRouter } from "@lib/i18n/navigation-pages-router";
import { liteClient as algoliasearch } from "algoliasearch/lite";
import { InstantSearch } from "react-instantsearch";
import { createInstantSearchRouterNext } from "react-instantsearch-router-nextjs";
import { useCurrentUser, useProductsLiked, useProductsRented } from "@hooks/use-current-user";
import initializeUiStateForHub from "@utils/catalog-filters/initializeUiStateForHub";
import initializeUiStateForListingPage from "@utils/catalog-filters/initializeUiStateForListingPage";
import parseQueryForUiState from "@utils/catalog-filters/parseQueryForUiState";
import forceUserSizeFiltersOnUiState from "@utils/catalog-filters/forceUserSizeFiltersOnUiState";
import forceUniqueSizeToSizeFilters from "@utils/catalog-filters/forceUniqueSizeToSizeFilters";
import computeQueryPathArray from "@utils/catalog-filters/computeQueryPathArray";
import isDifferenceOnRouterQuery from "@utils/catalog-filters/isDifferenceOnRouterQuery";
import getInitPersonalised from "@utils/catalog-filters/getInitPersonalised";
import BackToTop from "./BackToTop";
import SwapUserFeedback from "./SwapUserFeedback";
import CatalogGrid from "./CatalogGrid";
import CatalogPagination from "./CatalogPagination";
import CatalogFilters from "./CatalogFilters";
import LowResultsSection from "./LowResultsSection";
import CatalogProductsViewed from "./CatalogProductsViewed";
import CatalogSearchResults from "./CatalogSearchResults";
import CatalogHighlights from "./CatalogHighlights";
import catalogContexts from "@utils/constants/catalogContexts";

const client = algoliasearch(
  process.env.NEXT_PUBLIC_ALGOLIA_APP_ID,
  process.env.NEXT_PUBLIC_ALGOLIA_SEARCH_API_KEY,
);

const mainIndex = process.env.NEXT_PUBLIC_ALGOLIA_MAIN_INDEX_V2;

/* router's logic:
  https://www.algolia.com/doc/api-reference/widgets/instantsearch-next-router/react/
  https://www.algolia.com/doc/api-reference/widgets/history-router/react/

  We don't rely on Algolia router's logic.

  - 1. When arriving on the page, parseURL is called. It's not a route changing, so we initialize the uiState and we also store it inside a ref.
  - 2. When the user changes the filters, onStateChange is called. We update the uiState. We also store it inside a ref.
  - 3. When the filters changes, createURL is called and returns the new url which represents the new state of the filters.
       Afterwards, push is called to update the url of the browser. Now, the user can reload the page and should have the same results as before.
  - 4. When the user navigates to a new page:
    - 4.1. If it's a different page (hub to listing_page or listing_page to hub): InstantSearch is unmounted and mounted again and we're back to step 1.
    - 4.2. If it's the same page (hub to hub or listing_page to listing_page): we're still on the same session of InstantSearch, so parseURL is not called.
           There is a useEffect inside CatalogFilters that is responsible for updating the uiState.
           - 4.2.1. Navigating from hub to hub (different hub) or listing_page to listing_page (different listing_page) shouldReinitializeUiState checks the hub or listing_page slug already applied
           - 4.2.2. Navigating from hub to hub (same hub) or listing_page to listing_page (same listing_page) shouldReinitializeUiState checks the router.query and compares it to the uiState
  - 5. When the user navigates back to a previous page, parseURL is called before leaving the page.
       We know it's a route changing so we return the stored uiState in order to prevent doing a useless search.

  Personalised filters specificities: works with useConfigure.
  We need a useState to know which one is selected for the input radio (cf checkedFilters).
  We need a useState to know the actual query to pass to useConfigure (cf filtersConfigure).
  We need a useRef to know which one is selected in order to compute the url (cf personalisedRef).
*/

const Catalog = ({
  context,
  hub,
  listingPage,
  seoContent = "",
  url,
  userListProducts = [],
  onProductRemovedFromUserList,
  noHighlights = false,
  noLowResultSection = false,
}) => {
  const user = useCurrentUser();
  const productsLiked = useProductsLiked();
  const productsRented = useProductsRented();
  const router = useRouter();

  const { initCheckedPersonalised, initFiltersConfigure } = getInitPersonalised(
    listingPage?.search_query,
    router?.query,
    context,
    productsRented,
    productsLiked,
    userListProducts,
  );

  const appliedHubOrListingPageSlug = useRef();
  const appliedUiState = useRef({});
  const isRouteChanging = useRef(false);
  const personalisedRef = useRef(initCheckedPersonalised);

  const [checkedPersonalised, setCheckedPersonalised] = useState(initCheckedPersonalised);
  const [filtersConfigure, setFiltersConfigure] = useState(initFiltersConfigure);

  function shouldReinitializeUiState(context, routerQuery, uiState, listingPage) {
    if (appliedHubOrListingPageSlug.current === undefined) return false;
    if (context === catalogContexts.userlist) return false;

    if (
      appliedHubOrListingPageSlug.current !== listingPage?.slug &&
      appliedHubOrListingPageSlug.current !== hub?.slug
    ) {
      return true;
    }

    if (
      isDifferenceOnRouterQuery(context, routerQuery, uiState, listingPage, personalisedRef.current)
    ) {
      return true;
    }

    return false;
  }

  function resetPersonalisedFilters() {
    const { initCheckedPersonalised, initFiltersConfigure } = getInitPersonalised(
      listingPage?.search_query,
      {},
      context,
      productsRented,
      productsLiked,
      userListProducts,
    );

    updatePersonalisedFilters(initCheckedPersonalised, initFiltersConfigure);
  }

  function setAppliedHubOrListingPageSlug() {
    appliedHubOrListingPageSlug.current = listingPage?.slug || hub?.slug;
  }

  function initializeUiState(uiState) {
    if (!listingPage) {
      return initializeUiStateForHub(uiState, context);
    }

    return initializeUiStateForListingPage(uiState, listingPage, context);
  }

  function getCreateURL(routeState, location) {
    const baseUrl = getDelocalizedPath(new URL(location.href).pathname);

    const queryPathArray = computeQueryPathArray(
      routeState,
      listingPage,
      personalisedRef.current,
      context,
    );
    const queryPath = queryPathArray.length > 0 ? `?${queryPathArray.join("&")}` : "";

    return `${baseUrl}${queryPath}`;
  }

  function getInitializedUiState(location) {
    const uiState = {
      [mainIndex]: {
        refinementList: {},
      },
    };

    initializeUiState(uiState);
    parseQueryForUiState(uiState, location, context);
    forceUserSizeFiltersOnUiState(uiState, user?.size_filters, context);
    forceUniqueSizeToSizeFilters(uiState, mainIndex);

    setAppliedHubOrListingPageSlug();

    return uiState;
  }

  const onStateChange = ({ uiState, setUiState }) => {
    forceUniqueSizeToSizeFilters(uiState, mainIndex);
    appliedUiState.current = uiState;

    setUiState(uiState);
  };

  function onPushNewUrl(newUrl) {
    window.scrollTo({ top: 0, behavior: "smooth" });
    router.replace(newUrl, undefined, { shallow: true });
  }

  function forceUrlUpdate() {
    const newUrl = getCreateURL(appliedUiState.current, window.location);

    onPushNewUrl(newUrl);
  }

  function updatePersonalisedFilters(newCheckedPersonalised, newFiltersConfigure) {
    personalisedRef.current = newCheckedPersonalised;
    setFiltersConfigure(newFiltersConfigure);
    setCheckedPersonalised(newCheckedPersonalised);

    forceUrlUpdate();
  }

  useEffect(() => {
    const handleRouteChange = () => {
      isRouteChanging.current = true;
    };

    router.events.on("routeChangeStart", handleRouteChange);

    return () => {
      router.events.off("routeChangeStart", handleRouteChange);
    };
  }, [router?.events]);

  return (
    <InstantSearch
      searchClient={client}
      indexName={mainIndex}
      onStateChange={onStateChange}
      routing={{
        router: createInstantSearchRouterNext({
          serverUrl: url,
          singletonRouter,
          routerOptions: {
            cleanUrlOnDispose: false,
            createURL: function createURL({ routeState, location }) {
              return getCreateURL(routeState, location);
            },
            parseURL: function parseURL({ location }) {
              if (isRouteChanging.current) {
                isRouteChanging.current = false;

                return appliedUiState.current;
              } else {
                const uiState = getInitializedUiState(location);
                appliedUiState.current = uiState;

                return uiState;
              }
            },
            push: function push(newUrl) {
              onPushNewUrl(newUrl);
            },
          },
        }),
      }}
      insights={true}
      future={{
        preserveSharedStateOnUnmount: true,
      }}
    >
      <CatalogProductsViewed appliedHubOrListingPageSlug={appliedHubOrListingPageSlug} />
      {!noHighlights && (
        <CatalogHighlights
          hub={hub}
          listingPage={listingPage}
        />
      )}
      <div>
        <div className="catalog-container">
          <div className="container catalog d-flex flex-column">
            <div className="flex-grow-1 d-flex flex-column">
              <CatalogFilters
                context={context}
                listingPage={listingPage}
                getInitializedUiState={getInitializedUiState}
                shouldReinitializeUiState={shouldReinitializeUiState}
                resetPersonalisedFilters={resetPersonalisedFilters}
                checkedPersonalised={checkedPersonalised}
                filtersConfigure={filtersConfigure}
                updatePersonalisedFilters={updatePersonalisedFilters}
                userListProducts={userListProducts}
              />
              <div className="d-flex w-100 justify-content-center align-items-end">
                <CatalogSearchResults
                  getInitializedUiState={getInitializedUiState}
                  updatePersonalisedFilters={updatePersonalisedFilters}
                />
              </div>

              <div>
                <CatalogGrid
                  context={context}
                  onProductRemovedFromUserList={onProductRemovedFromUserList}
                />
                <CatalogPagination />
              </div>
            </div>
          </div>
        </div>
        <LowResultsSection
          noLowResultSection={noLowResultSection}
          context={context}
          getInitializedUiState={getInitializedUiState}
          resetPersonalisedFilters={resetPersonalisedFilters}
          hub={hub}
          listingPage={listingPage}
        />
        {seoContent && (
          <div
            className="mb-0 mt-4 font-size-sm px-3 pb-5"
            dangerouslySetInnerHTML={{ __html: seoContent }}
          />
        )}
        <BackToTop />
        <SwapUserFeedback context={context} />
      </div>
    </InstantSearch>
  );
};

export default Catalog;
